import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const slides = [
  {
    image:
      "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/f973d73a-6d47-4afa-84ae-304624acd259_compressed_-1.webp",
    alt: "GH Corporation Korean beauty brand products showcase - ANUA, Torriden, Dr. Althea, Centellian24",
  },
  {
    image:
      "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/138028dd-6e2f-475c-90b6-784106d9c846_compressed_-6.webp",
    alt: "GH Corporation cosmetics export warehouse and logistics operations",
  },
  {
    image:
      "https://storage.readdy-site.link/project_files/35b57ced-d75b-4532-9a59-9b08ce476502/1adf84c5-8836-46b5-9ddb-5edf993b6674_compressed_-3.webp",
    alt: "GH Corporation multi-brand Korean cosmetics product lineup - SO.NATURAL, JUMISO, lilyeve, Hetras",
  },
];

const SLIDE_INTERVAL = 5000;
const FADE_DURATION = 1200;

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);

  const goTo = useCallback((index: number) => {
    setPrev(current);
    setCurrent(index);
  }, [current]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [current, goTo]);

  return (
    <section
      id="home"
      className="relative w-full min-h-[760px] md:min-h-[860px] flex items-center overflow-hidden"
    >
      {/* Slides */}
      {slides.map((slide, i) => (
        <img
          key={i}
          src={slide.image}
          alt={slide.alt}
          title="GH Korean cosmetics export"
          className="absolute inset-0 w-full h-full object-cover object-top transition-opacity"
          style={{
            opacity: i === current ? 1 : i === prev ? 1 : 0,
            transitionDuration: `${FADE_DURATION}ms`,
            zIndex: i === current ? 2 : i === prev ? 1 : 0,
          }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/60 z-[3]" />

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-32 pb-20">
        <div className="max-w-5xl gh-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-background-50/40 backdrop-blur-sm mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400 inline-block" />
            <span className="text-background-50/95 font-label text-[11px] tracking-[0.22em] uppercase">
              KOREA-BASED K-BEAUTY EXPORT PARTNER
            </span>
          </div>

          <h1 className="font-heading text-background-50 text-5xl md:text-7xl lg:text-[88px] leading-[1.02] font-light tracking-tight">
            Korean Beauty,
            <br />
            <span className="italic font-normal text-background-100">Sourced and Shipped Worldwide.</span>
          </h1>

          <p className="mt-8 max-w-2xl text-background-100/95 text-base md:text-lg leading-relaxed font-light">
            GH Corporation supplies authentic Korean cosmetics from 125+ leading brands to importers, distributors and retailers worldwide. From flexible multi-brand sourcing to FCL, LCL and air freight, we manage your order from Korea to destination.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="whitespace-nowrap cursor-pointer group inline-flex items-center gap-4 pl-6 pr-2 py-2 rounded-full bg-background-50 text-foreground-950 hover:bg-accent-100 transition-colors"
            >
              <span className="font-label text-sm tracking-[0.18em] uppercase">
                Request a Wholesale Quote
              </span>
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 group-hover:bg-accent-500 transition-colors">
                <i className="ri-arrow-right-up-line text-base" />
              </span>
            </Link>
            <Link
              to="/brands"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-background-50/50 text-background-50 hover:bg-background-50/10 transition-colors"
            >
              <span className="font-label text-sm tracking-[0.18em] uppercase">
                View Our Brands
              </span>
              <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </div>

        <div className="mt-8 w-full max-w-3xl">
          <p className="text-background-50/80 text-[13px] md:text-sm font-label tracking-[0.14em] uppercase text-center md:text-left">
            Authentic Products · Mixed Orders · Export Documentation · Global Logistics
          </p>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-background-50/15 border border-background-50/15 rounded-2xl overflow-hidden backdrop-blur-[2px]">
          {[
            { num: "125+", label: "Korean Beauty Brands" },
            { num: "8+", label: "Export Markets" },
            { num: "Mixed Order", label: "Multi-Brand Sourcing" },
            { num: "FCL · LCL · AIR", label: "Freight Options" },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-foreground-950/30 px-6 py-7 md:px-8 md:py-8"
            >
              <div className="font-heading text-background-50 text-3xl md:text-4xl leading-none">
                {s.num}
              </div>
              <div className="mt-3 font-label text-background-100/80 text-[11px] tracking-[0.2em] uppercase">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Slide Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-5">
        <div className="flex items-center gap-2.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className={`cursor-pointer rounded-full transition-all duration-500 ${
                i === current
                  ? "w-6 h-1.5 bg-background-50"
                  : "w-1.5 h-1.5 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
        <span className="font-label text-[10px] tracking-[0.3em] uppercase text-background-50/50">
          Scroll
        </span>
      </div>
    </section>
  );
}